import PropTypes from 'prop-types';
import { use, useCallback, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { v4 as uuid } from 'uuid';

import { getDefaultValueForType, getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';
import Tooltip from '../../../components/Tooltip/Tooltip';

import ContentObjectFormContext from '../../../contexts/ContentObjectFormContext';
import { useModals } from '../../../contexts/ModalContext';

import { CaretUpIcon, QuestionMarkCircleIcon } from '../../../images/shapes';

import { useFieldContext, useFormContext } from '../../form';

const swapIds = (array, oldIdx, newIdx) => {
  const oldValue = array[oldIdx];
  const newValue = array[newIdx];
  array[oldIdx] = newValue;
  array[newIdx] = oldValue;
};

const listItemPropItemsProps = {};
const listItemPropItemsSchema = {};

const ListField = ({
  label = '',
  required = false,
  itemsProps = listItemPropItemsProps,
  itemsSchema = listItemPropItemsSchema,
  error,
  helpText,
  disabled = false,
  readonly = false,
  isCtdForm = false,
  additionalClasses,
  testId,
}) => {
  const form = useFormContext();
  const field = useFieldContext();
  const { t } = useTranslation();
  const modal = useModals();

  const [open, toggleOpen] = useReducer((open) => !open, true);

  const { isPatchable } = use(ContentObjectFormContext);
  const value = useMemo(() => field.state.value || [], [field.state.value]);

  const arrayIdsRefs = useRef(value.map(() => uuid()));

  const addProperty = useCallback(() => {
    const newObject = itemsProps.order.reduce((item, key) => {
      item[key] =
        itemsSchema.properties[key].default ||
        getDefaultValueForType(itemsSchema.properties[key].type);
      return item;
    }, {});
    field.pushValue(newObject);
    arrayIdsRefs.current.push(uuid());
  }, [itemsProps.order, field, itemsSchema.properties]);

  const renderListField = useCallback(
    (key, itemName) => {
      const itemProps = itemsProps.propertiesConfig[itemName];
      const itemSchema = itemsSchema.properties[itemName];
      const isRequired =
        !isPatchable &&
        itemsSchema.required?.findIndex((key) => key === itemName) > -1;

      const label =
        isCtdForm && itemProps.hidden ? (
          <div className="inline-flex items-center gap-1">
            {itemProps.label || itemName}
            <Tooltip
              tooltip={t('PropertyForm.HiddenListPropTooltip')}
              tooltipPlacement="topLeft"
              phoneTooltipPlacement="topLeft"
            >
              <QuestionMarkCircleIcon className="w-3 text-blue" />
            </Tooltip>
          </div>
        ) : null;

      return (
        <form.AppField key={key} name={key}>
          {(subField) => (
            <subField.CtoCustomField
              name={key}
              properties={itemProps}
              schema={itemSchema}
              isRequired={isRequired}
              disabled={disabled}
              readonly={readonly}
              label={label}
              additionalClasses="max-w-3xl"
              isCtdForm={isCtdForm}
              testId={testId}
            />
          )}
        </form.AppField>
      );
    },
    [
      itemsProps.propertiesConfig,
      itemsSchema.properties,
      itemsSchema.required,
      isPatchable,
      isCtdForm,
      t,
      disabled,
      readonly,
      testId,
    ],
  );

  const handleOrderUp = useCallback(
    (idx) => {
      field.swapValues(idx - 1, idx);
      swapIds(arrayIdsRefs.current, idx, idx - 1);
    },
    [field],
  );

  const handleOrderDown = useCallback(
    (idx) => {
      field.swapValues(idx + 1, idx);
      swapIds(arrayIdsRefs.current, idx, idx + 1);
    },
    [field],
  );

  const handleDeleteItem = useCallback(
    async (idx) => {
      const result = await modal.delete(t('ContentForm.RemoveItem'));
      if (!result) return;
      field.removeValue(idx);
      arrayIdsRefs.current.splice(idx, 1);
    },
    [field, modal, t],
  );

  return (
    <div
      className={twMerge(
        'bg-white dark:bg-transparent px-0',
        additionalClasses,
      )}
    >
      <label
        className="block w-full text-sm text-slate-400 dark:text-gray-200 pb-2.5"
        {...getTestProps(testId, 'label')}
      >
        {label}
        {required && <RequiredTemplate />}
        {isCtdForm && (
          <Button
            onClick={toggleOpen}
            buttonColor="borderless"
            buttonSize="xs"
            iconImage={
              <CaretUpIcon
                className={twMerge(
                  'w-2 rotate-180 text-blue',
                  open ? 'rotate-0' : 'rotate-180',
                )}
                {...getTestProps(testId, 'list-default-open')}
              />
            }
            additionalIconClasses="ml-1"
            noPaddings
            additionalClasses="mr-2"
          >
            {t('ContentTypeForm.ShowProperties')}
          </Button>
        )}
      </label>
      <div
        className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2.5
        md:p-5 rounded-lg"
      >
        <div className="relative">
          {open && value?.length > 0 && (
            <div className="mb-2 space-y-2">
              {value.map((_, idx, currentValue) => {
                const fieldName = `${field.name}[${idx}]`;
                return (
                  <form.AppField key={fieldName} name={fieldName}>
                    {(subField) => (
                      <subField.ListFieldItem
                        key={arrayIdsRefs.current[idx]}
                        label={label}
                        itemsProps={itemsProps}
                        currentValue={currentValue}
                        idx={idx}
                        renderListField={renderListField}
                        handleOrderUp={handleOrderUp}
                        handleOrderDown={handleOrderDown}
                        handleDeleteItem={handleDeleteItem}
                        disabled={disabled || readonly}
                        isCtdForm={isCtdForm}
                        {...getTestProps(testId, fieldName, 'testId')}
                      />
                    )}
                  </form.AppField>
                );
              })}
            </div>
          )}
          {(!isCtdForm || !value.length) && (
            <Button
              onClick={addProperty}
              buttonColor="blueBordered"
              buttonSize="xs"
              disabled={disabled || readonly}
              {...getTestProps(testId, 'add-item', 'testId')}
            >
              {t('ContentForm.AddItem')}
            </Button>
          )}
        </div>
        <HelpErrorTextsTemplate
          helpText={helpText}
          error={typeof error === 'string' ? error : ''}
          testId={testId}
        />
      </div>
    </div>
  );
};

export default ListField;

ListField.propTypes = {
  /**
   * Array helpers from formik array field
   */
  arrayHelpers: PropTypes.object.isRequired,
  /**
   * Field label above items
   */
  label: PropTypes.node,
  /**
   * If field is required
   */
  required: PropTypes.bool,
  /**
   * List items properties
   */
  itemsProps: PropTypes.object,
  /**
   * List items schema
   */
  itemsSchema: PropTypes.object,
  /**
   * Errors for fields
   */
  error: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    ),
    PropTypes.string,
  ]),
  /**
   * Help text under items
   */
  helpText: PropTypes.node,
  /**
   * If form is disabled
   */
  disabled: PropTypes.bool,
  /**
   * If field is readonly (non interactive but without disabled classes)
   */
  readonly: PropTypes.bool,
  /**
   * Additional container classes
   */
  additionalClasses: PropTypes.string,
  /**
   * Test id for field
   */
  testId: PropTypes.string,
};
